import { useCallback } from 'react';
import type { ReactNode } from 'react';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { useTracking } from 'react-tracking';
import { Dropdown, ButtonRenderProps } from '@/components/Dropdown';
import { Button, Grid } from '@simplywallst/ui-core';
import {
  ChevronDownSmallSolid,
  TilesSolid,
  ListSolid,
} from '@simplywallst/ui-core/icons';
import { push } from 'redux-first-router';
import {
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
} from '@/constants/routes';
import {
  Meta,
  Wrapper,
  DropdownWrapper,
  ViewType,
  TotalRecords,
  ButtonWrapper,
} from './styled';
import useMarketOptions from '../../hooks/useMarketOptions';
import useIndustryOptions from '../../hooks/useIndustryOptions';
import useSortByOptions from '../../hooks/useSortByOptions';
import useUser from '@/hooks/useUser';
import { MarketIndustryFilter } from '@/components/Header';
import { getStocksPath } from '../../utils';

type SortBy =
  | 'market-cap-large'
  | 'market-cap-small'
  | 'top-gainers'
  | 'biggest-losers'
  | 'growth'
  | 'dividend-yield-high';

type StocksView = 'tile' | 'table';

export interface Props {
  market: PrimaryCountryISOElseGlobal;
  industry: string | 'any';
  sortBy?: SortBy;
  stocksView: StocksView;
  totalRecords?: number;
  onViewSelect: (view: StocksView) => void;
  onAdvancedFilterClick: () => void;
  children: ReactNode;
}

interface NavigateTo {
  market: PrimaryCountryISOElseGlobal;
  industry?: string | 'any';
  sortBy: SortBy;
}

const Header = ({
  children,
  market,
  industry,
  sortBy = 'market-cap-large',
  stocksView,
  totalRecords,
  onViewSelect,
  onAdvancedFilterClick,
}: Props) => {
  const { trackEvent } = useTracking();
  const { data: user } = useUser();

  const marketOptions = useMarketOptions(
    true,
    industry !== 'any' ? ROUTE_STOCKS_MARKET_INDUSTRY : ROUTE_STOCKS_MARKET,
    {
      industry,
      sortBy,
    }
  );
  const industryOptions = useIndustryOptions(true, market, {
    sortBy,
  });
  const sortByOptions = useSortByOptions(market, industry);

  const dropdownButton = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
      active,
    }: ButtonRenderProps) => {
      return (
        <ButtonWrapper active={active}>
          <Button
            styleType="passiveLarge"
            alignIcon="right"
            onBlur={onBlur}
            onFocus={onFocus}
            onClick={onClick}
            ref={innerRef}
            data-cy-id={name}
          >
            {value}
            <ChevronDownSmallSolid />
          </Button>
        </ButtonWrapper>
      );
    },
    []
  );

  const handleTableView = useCallback(() => {
    onViewSelect('table');
    trackEvent({
      action: 'click',
      subject: 'table-view',
    });
  }, [onViewSelect, trackEvent]);

  const handleTileView = useCallback(() => {
    onViewSelect('tile');
    trackEvent({
      action: 'click',
      subject: 'tile-view',
    });
  }, [onViewSelect, trackEvent]);

  const navigateTo = useCallback(
    ({ market, industry, sortBy }: NavigateTo) => {
      const path = getStocksPath({
        market,
        industry,
        sortBy,
        excludePageQuery: user.loggedIn,
      });
      if (typeof path === 'undefined') return;
      try {
        push(path);
      } catch (e) {
        console.warn(e);
      }
    },
    [user.loggedIn]
  );

  const handlerMarketChange = useCallback(
    (market: string) => {
      navigateTo({
        market: market as PrimaryCountryISOElseGlobal,
        industry,
        sortBy,
      });
    },
    [industry, sortBy, navigateTo]
  );

  const handleIndustryChange = useCallback(
    (industry: string) => {
      navigateTo({
        market,
        industry,
        sortBy,
      });
    },
    [market, sortBy, navigateTo]
  );

  const handleSortByChange = useCallback(
    (sortBy: string) => {
      navigateTo({
        market,
        industry,
        sortBy: sortBy as SortBy,
      });
    },
    [market, industry, navigateTo]
  );

  return (
    <Wrapper as="header">
      {/* IMPORTANT: Meta should ideally render first in the markup since it is (usually) the page LCP */}
      <Meta row="2">{children}</Meta>
      <MarketIndustryFilter
        marketOptions={marketOptions}
        industryOptions={industryOptions}
        selectedMarket={market}
        selectedIndustry={industry}
        onMarketChange={handlerMarketChange}
        onIndustryChange={handleIndustryChange}
        onAdvancedFilterClick={onAdvancedFilterClick}
      />
      <Grid templateColumns="auto auto" templateColumnsMd="250px auto 250px">
        <DropdownWrapper>
          <Dropdown
            name="stock-sorting"
            displayType="block"
            align="left"
            options={sortByOptions}
            value={sortBy}
            onSelect={handleSortByChange}
            buttonRender={dropdownButton}
          />
        </DropdownWrapper>
        <Grid
          justifyContent="center"
          alignContent="center"
          row="2"
          column="span 2"
          rowMd="1"
          columnMd="2"
        >
          {totalRecords && (
            <TotalRecords>
              {totalRecords.toLocaleString()} companies
            </TotalRecords>
          )}
        </Grid>
        <Grid display="grid" templateColumns="auto auto" justifyContent="end">
          <ViewType
            data-cy-id="table-view"
            styleType="passiveLarge"
            disabled={stocksView === 'table'}
            onClick={handleTableView}
          >
            <ListSolid />
          </ViewType>
          <ViewType
            data-cy-id="tile-view"
            styleType="passiveLarge"
            disabled={stocksView === 'tile'}
            onClick={handleTileView}
          >
            <TilesSolid />
          </ViewType>
        </Grid>
      </Grid>
    </Wrapper>
  );
};

export default Header;
